import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';

export default function ResetWithCodePage() {
  const { toast } = useToast();
  const [location, setLocation] = useLocation();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const e = params.get('email') || '';
      if (e) setEmail(e);
    } catch {}
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!email || !code || !password) {
      toast({ title: 'Missing info', description: 'Enter email, code, and a new password', variant: 'destructive' });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: 'Passwords do not match', description: 'Please re-enter', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const resp = await fetch('/api/auth/confirm-reset-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, code, newPassword: password }),
      });
      if (!resp.ok) {
        const msg = (await resp.json().catch(() => ({}))).message || 'Reset failed';
        throw new Error(msg);
      }
      toast({ title: 'Password updated', description: 'You are all set!' });
      setLocation('/', { replace: true });
    } catch (err: any) {
      toast({ title: 'Reset failed', description: err?.message || 'Please try again', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-gradient-to-br from-blue-50 via-white to-blue-100 pt-safe pb-safe">
      <div className="w-full max-w-md px-4 py-8">
        <Card className="w-full backdrop-blur-sm bg-white/80 shadow-xl border-0 rounded-2xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              Reset with Code
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Email</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={submitting} required className="transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Code</label>
                <Input inputMode="numeric" pattern="[0-9]*" value={code} onChange={(e) => setCode(e.target.value)} disabled={submitting} required className="transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">New Password</label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={submitting} required className="transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Confirm Password</label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={submitting} required className="transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600" />
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>{submitting ? 'Resetting…' : 'Reset Password'}</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


